/**
 * useThemeImage Hook
 * React hook to get theme-aware image URLs
 * Automatically updates when theme changes
 */

import { useState, useEffect } from 'react'
import { getThemeImage } from '@/config/images.config'

export interface ThemeImageConfig {
  light: string
  dark?: string
}

/**
 * Hook to get theme-aware image URL
 * @param imageConfig - Image configuration with light and optional dark URLs
 * @returns Current theme-appropriate image URL
 */
export function useThemeImage(imageConfig: ThemeImageConfig): string {
  const [imageUrl, setImageUrl] = useState<string>(() => {
    if (typeof window === 'undefined') {
      return imageConfig.light
    }
    const isDarkMode = document.documentElement.classList.contains('dark')
    return getThemeImage(imageConfig.light, imageConfig.dark, isDarkMode)
  })

  useEffect(() => {
    if (typeof window === 'undefined') return

    const updateImage = () => {
      const isDarkMode = document.documentElement.classList.contains('dark')
      const url = getThemeImage(imageConfig.light, imageConfig.dark, isDarkMode)
      setImageUrl(url)
    }

    // Initial check
    updateImage()

    // Watch for theme changes
    const observer = new MutationObserver(updateImage)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    })

    // Also listen to system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleSystemThemeChange = () => {
      // Only update if we're in system mode (check if dark class exists)
      updateImage()
    }
    mediaQuery.addEventListener('change', handleSystemThemeChange)

    return () => {
      observer.disconnect()
      mediaQuery.removeEventListener('change', handleSystemThemeChange)
    }
  }, [imageConfig.light, imageConfig.dark])

  return imageUrl
}

